import {useEffect} from "react";
import {css, Theme} from "@emotion/react";

import {flexAlignCenter} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {usePrimaryClick} from "../../../hooks/use_primary_click";
import {PoiItemsList} from "../../../maps/components/PoiItemsList";
import {IPropertyListObj} from "../../../property_list/hooks/use_property_list";
import {
    galleryButtonClick,
    moreInfoLinkClick,
    quickViewNextPropertyClick,
    quickViewPrevPropertyClick,
    quickViewPropertyChange
} from "../../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {gtmClickGalleryButton, gtmClickPropertyMoreInfo} from "../../../tracking/google_tag_manager/gtm_event_map_quick_view_sidebar";
import {QWActionButtons} from "../../components/quick_view/QWActionButtons";
import {QWHeaderWithPagination} from "../../components/quick_view/QWHeaderWithPagination";
import {GalleryType, QWImageWithButton} from "../../components/quick_view/QWImageWithButton";
import {QWLocalizationInfo} from "../../components/quick_view/QWLocalizationInfo";
import {QWPictograms} from "../../components/quick_view/QWPictograms";
import {QWPropertyInfoHeading} from "../../components/quick_view/QWPropertyInfoHeading";
import {createOfferListingAmplitudePendingDetailUrl} from "../../list/helpers/offer_listing_amplitude_item_list_id";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {IMapOfferSidebar} from "../types/MapOfferSidebar";

interface IProps {
    handleQuickViewClose: () => void;
    setSelectedIndex: (index: number) => void;
    propertyList: IPropertyListObj;
    selectedIndex: number;
    propertiesRequestState: RequestState;
    offer: IMapOfferSidebar;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
}

export const MapOfferSidebarQuickView = (props: IProps) => {
    const {handleQuickViewClose, selectedIndex, setSelectedIndex, propertiesRequestState, offer, amplitudeItemListContext} = props;
    const {properties, requestState: listRequestState} = props.propertyList;
    const selectedProperty = selectedIndex > properties.length - 1 ? properties[0] : properties[selectedIndex];
    const isLoading = propertiesRequestState === RequestState.Waiting || listRequestState === RequestState.Waiting || !selectedProperty;

    useEffect(() => {
        selectedProperty?.id && quickViewPropertyChange(offer.id, selectedProperty.id);
    }, [selectedProperty]);

    const updatePropertyDetailLinkWithPendingContext = (event: React.MouseEvent<HTMLElement>) => {
        const anchor = event.currentTarget as HTMLAnchorElement;

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: offer.id,
            propertyId: selectedProperty.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const handleDetailsButtonClick = usePrimaryClick((event: React.MouseEvent<HTMLElement>) => {
        updatePropertyDetailLinkWithPendingContext(event);
        gtmClickPropertyMoreInfo();
        moreInfoLinkClick(offer.id, selectedProperty.id);
    });

    const handleGalleryButtonClick = (type: GalleryType) => {
        gtmClickGalleryButton(type);
        galleryButtonClick(type, offer.id, selectedProperty.id);
    };

    const nextClickAnalyticsEvent = () => {
        quickViewNextPropertyClick();
    };

    const prevClickAnalyticsEvent = () => {
        quickViewPrevPropertyClick();
    };

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";

    const propertyPlanImage = selectedProperty.plan_image_pages[0]?.image.p_img_760x428;
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerId: offer.id,
        offerSlug: offer.slug,
        propertyId: selectedProperty.id
    });

    return (
        <div css={quickViewContainer}>
            {isLoading ? (
                <div css={[flexAlignCenter, topLoaderContainer]}>
                    <Loader fullWidth size="lg" />
                </div>
            ) : (
                <>
                    <QWHeaderWithPagination
                        handleQuickViewClose={handleQuickViewClose}
                        setSelectedIndex={setSelectedIndex}
                        propertyList={props.propertyList}
                        selectedIndex={selectedIndex}
                        onNextClickEvent={nextClickAnalyticsEvent}
                        onPrevClickEvent={prevClickAnalyticsEvent}
                    />

                    <QWImageWithButton
                        alt={offer.name}
                        firstImageSrc={propertyPlanImage}
                        secondImageSrc={offer.main_image.m_img_760x428}
                        clickEvent={handleGalleryButtonClick}
                        onPropertyLinkClick={updatePropertyDetailLinkWithPendingContext}
                        propertyLink={propertyLink}
                    />

                    <QWPropertyInfoHeading rooms={selectedProperty.rooms} offerType={offer.type} offerName={offer.name} />
                </>
            )}

            <div css={bottomContainerPadding}>
                {/*Those two components are showing offer info so they don't need to be recompiled every time when property change */}
                <QWLocalizationInfo address={offer.address} distanceFromRegion={offer.stats?.distance_from_region} offerType={offer.type} />

                <PoiItemsList offer={offer} />

                {isLoading ? (
                    <Loader fullWidth size="lg" />
                ) : (
                    <>
                        <QWPictograms selectedProperty={selectedProperty} />

                        <QWActionButtons
                            vendorId={offer.vendor.id}
                            offerId={offer.id}
                            propertyId={selectedProperty.id}
                            propertyPrice={selectedProperty.price ?? null}
                            propertyLink={propertyLink}
                            applicationSource={ApplicationSource.QuickViewPropertyModal}
                            applicationSourceSection={ApplicationSourceSection.SIDEBAR}
                            clickEvent={handleDetailsButtonClick}
                            hideApplicationButton={isForSaleOverLimit}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const bottomContainerPadding = css`
    padding: 0 1.6rem 3.2rem 1.6rem;
`;

const quickViewContainer = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    background-color: ${theme.colors.gray[100]};
    padding: 0;
`;

const topLoaderContainer = css`
    height: 51rem;
`;
import {useEffect} from "react";
import {css, Theme} from "@emotion/react";

import {flexAlignCenter} from "@pg-design/helpers-css";
import {Loader} from "@pg-design/loader-module";
import {RequestState} from "@pg-mono/request-state";
import {rpAppLink} from "@pg-mono/rp-routes";

import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {usePrimaryClick} from "../../../hooks/use_primary_click";
import {PoiItemsList} from "../../../maps/components/PoiItemsList";
import {IPropertyListObj} from "../../../property_list/hooks/use_property_list";
import {
    galleryButtonClick,
    moreInfoLinkClick,
    quickViewNextPropertyClick,
    quickViewPrevPropertyClick,
    quickViewPropertyChange
} from "../../../tracking/algolytics_hits/map_quick_view_sidebar_hit";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {gtmClickGalleryButton, gtmClickPropertyMoreInfo} from "../../../tracking/google_tag_manager/gtm_event_map_quick_view_sidebar";
import {QWActionButtons} from "../../components/quick_view/QWActionButtons";
import {QWHeaderWithPagination} from "../../components/quick_view/QWHeaderWithPagination";
import {GalleryType, QWImageWithButton} from "../../components/quick_view/QWImageWithButton";
import {QWLocalizationInfo} from "../../components/quick_view/QWLocalizationInfo";
import {QWPictograms} from "../../components/quick_view/QWPictograms";
import {QWPropertyInfoHeading} from "../../components/quick_view/QWPropertyInfoHeading";
import {createOfferListingAmplitudePendingDetailUrl} from "../../list/helpers/offer_listing_amplitude_item_list_id";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {IMapOfferSidebar} from "../types/MapOfferSidebar";

interface IProps {
    handleQuickViewClose: () => void;
    setSelectedIndex: (index: number) => void;
    propertyList: IPropertyListObj;
    selectedIndex: number;
    propertiesRequestState: RequestState;
    offer: IMapOfferSidebar;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
}

export const MapOfferSidebarQuickView = (props: IProps) => {
    const {handleQuickViewClose, selectedIndex, setSelectedIndex, propertiesRequestState, offer, amplitudeItemListContext} = props;
    const {properties, requestState: listRequestState} = props.propertyList;
    const selectedProperty = selectedIndex > properties.length - 1 ? properties[0] : properties[selectedIndex];
    const isLoading = propertiesRequestState === RequestState.Waiting || listRequestState === RequestState.Waiting || !selectedProperty;

    useEffect(() => {
        selectedProperty?.id && quickViewPropertyChange(offer.id, selectedProperty.id);
    }, [selectedProperty]);

    const updatePropertyDetailLinkWithPendingContext = (event: React.MouseEvent<HTMLElement>) => {
        const anchor = event.currentTarget as HTMLAnchorElement;

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: offer.id,
            propertyId: selectedProperty.id,
            itemListContext: amplitudeItemListContext
        });
    };

    const handleDetailsButtonClick = usePrimaryClick((event: React.MouseEvent<HTMLElement>) => {
        updatePropertyDetailLinkWithPendingContext(event);
        gtmClickPropertyMoreInfo();
        moreInfoLinkClick(offer.id, selectedProperty.id);
    });

    const handleGalleryButtonClick = (type: GalleryType) => {
        gtmClickGalleryButton(type);
        galleryButtonClick(type, offer.id, selectedProperty.id);
    };

    const nextClickAnalyticsEvent = () => {
        quickViewNextPropertyClick();
    };

    const prevClickAnalyticsEvent = () => {
        quickViewPrevPropertyClick();
    };

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";

    const propertyPlanImage = selectedProperty.plan_image_pages[0]?.image.p_img_760x428;
    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerId: offer.id,
        offerSlug: offer.slug,
        propertyId: selectedProperty.id
    });

    return (
        <div css={quickViewContainer}>
            {isLoading ? (
                <div css={[flexAlignCenter, topLoaderContainer]}>
                    <Loader fullWidth size="lg" />
                </div>
            ) : (
                <>
                    <QWHeaderWithPagination
                        handleQuickViewClose={handleQuickViewClose}
                        setSelectedIndex={setSelectedIndex}
                        propertyList={props.propertyList}
                        selectedIndex={selectedIndex}
                        onNextClickEvent={nextClickAnalyticsEvent}
                        onPrevClickEvent={prevClickAnalyticsEvent}
                    />

                    <QWImageWithButton
                        alt={offer.name}
                        firstImageSrc={propertyPlanImage}
                        secondImageSrc={offer.main_image.m_img_760x428}
                        clickEvent={handleGalleryButtonClick}
                        onPropertyLinkClick={updatePropertyDetailLinkWithPendingContext}
                        propertyLink={propertyLink}
                    />

                    <QWPropertyInfoHeading rooms={selectedProperty.rooms} offerType={offer.type} offerName={offer.name} />
                </>
            )}

            <div css={bottomContainerPadding}>
                {/*Those two components are showing offer info so they don't need to be recompiled every time when property change */}
                <QWLocalizationInfo address={offer.address} distanceFromRegion={offer.stats?.distance_from_region} offerType={offer.type} />

                <PoiItemsList offer={offer} />

                {isLoading ? (
                    <Loader fullWidth size="lg" />
                ) : (
                    <>
                        <QWPictograms selectedProperty={selectedProperty} />

                        <QWActionButtons
                            vendorId={offer.vendor.id}
                            offerId={offer.id}
                            propertyId={selectedProperty.id}
                            propertyPrice={selectedProperty.price ?? null}
                            propertyLink={propertyLink}
                            applicationSource={ApplicationSource.QuickViewPropertyModal}
                            applicationSourceSection={ApplicationSourceSection.SIDEBAR}
                            clickEvent={handleDetailsButtonClick}
                            hideApplicationButton={isForSaleOverLimit}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const bottomContainerPadding = css`
    padding: 0 1.6rem 3.2rem 1.6rem;
`;

const quickViewContainer = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    background-color: ${theme.colors.gray[100]};
    padding: 0;
`;

const topLoaderContainer = css`
    height: 51rem;
`;
